import { ConfigManager } from "@omni/config";
import { ComponentsConfig } from "@omni/schemas/config/componentsConfig.schema";
import { ConfigType } from "@omni/types/enums/configType.enum";
import { Locale } from "@omni/types/enums/locale.enum";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import * as LocaleUtils from "@omni/utils/locale.utils";

function getLocale() {
    const locale = ConfigManager.getConfig(ConfigType.Components).general
        .locale;

    return locale;
}

function getLanguageTag() {
    return LocaleUtils.getLanguageTag(getLocale());
}

function getLanguage() {
    return getLocale().substring(0, 2) as "nl" | "fr";
}

function getLoginPage() {
    const config = ConfigManager.getConfig(ConfigType.Components);

    const pageTemplate = config.general["page-template"];

    switch (pageTemplate) {
        case PageTemplate.Checkout:
            return config.pages["checkout-login"];
        default:
            return config.pages["login"];
    }
}

function isDevelopment() {
    return process.env.NODE_ENV === "development";
}

type Methods = {
    getLocale(): Locale;
    getLanguageTag(): string;
    getLanguage(): "nl" | "fr";
    getLoginPage(): string;
    isDevelopment(): boolean;
};

const methods: Methods = {
    getLocale: getLocale,
    getLanguageTag: getLanguageTag,
    getLanguage: getLanguage,
    getLoginPage: getLoginPage,
    isDevelopment: isDevelopment
};

/**
 * DO NOT ASSIGN TO TOP LEVEL VARIABLES
 * The values won't be reevaluated when the config changes (eg. between requests in a server context)
 * Module federation only evaluates the module once
 */
export const Config = Object.freeze(
    // proxying the get call ensures the getComponentsConfig function is called when accessing a property
    // this is necessary when config is accessed in a request based setup such as a server

    new Proxy({} as ComponentsConfig & typeof methods, {
        get(_, prop: keyof (ComponentsConfig & typeof methods)) {
            if (prop in methods) {
                return methods[prop as keyof typeof methods];
            } else {
                return ConfigManager.getConfig(ConfigType.Components)[
                    prop as keyof ComponentsConfig
                ];
            }
        }
    })
);
